import type { ConsentType } from "./hooks"

// Telehealth consent copy for the consumer booking flow (MEM-755).
//
// Literal strings (no react-intl): this package isn't in the intl extraction pipeline and the
// experience is English-only for the foreseeable future. Keyed by consent type so each consent's copy
// is unambiguous.
//
// ⚠ This is legal consent copy — change it deliberately. Consent signoffs are recorded with only a
// timestamp (no stored version), so "what a member agreed to" is reconstructed from this file's
// git/deploy history at their signedAt. If Legal/Bridge ever require an explicit version, reintroduce
// one here + on the signConsent payload + in the stored record (see MEM-756).

type ConsentEntry = {
  // The read-only copy section the member reads for this consent.
  section: { heading: string; body: string }
  // The required checkbox that acknowledges it. `name` backs the form field.
  clause: { name: string; label: string; requiredMessage: string }
}

export type ConsentDocument = {
  title: string
  intro: string
  // Both consent types must have copy — a missing one fails to compile.
  consents: Record<ConsentType, ConsentEntry>
  // The typed-name signature ratifies both consents (a shared ceremony, not its own consent). Per
  // MEM-755 the typed name is client-side gating only — never sent or stored. `attestation` is the
  // legally-required lead-in above the field; `helper` sits below it; `signedOnLabel` builds the
  // "Signed on {date}" line.
  signature: {
    attestation: string
    label: string
    helper: string
    requiredMessage: string
  }
}

export const consentDocument: ConsentDocument = {
  title: "Consent to telehealth services",
  intro: "Please read and agree to the following before booking your appointment.",
  consents: {
    telehealth_informed_consent: {
      section: {
        heading: "Telehealth visit",
        body: "Your appointment will take place over a secure HIPAA-compliant video call. By agreeing, you consent to receiving care through this format and understand that your provider may determine an in-person visit is more appropriate for your needs."
      },
      clause: {
        name: "telehealthConsent",
        label:
          "I consent to a telehealth visit and understand that care is delivered by independent, board-certified clinicians.",
        requiredMessage: "Please confirm your consent to a telehealth visit."
      }
    },
    health_information_sharing: {
      section: {
        heading: "Your health information",
        body: "Information you share during intake and your visit will be used to provide care and stored securely in accordance with HIPAA. It won’t be shared with your employer."
      },
      clause: {
        name: "healthInfoAuthorization",
        label:
          "I authorize Carrot to share my relevant health and benefit information with the clinical team for this visit.",
        requiredMessage: "Please authorize sharing your information with the clinical team."
      }
    }
  },
  signature: {
    // Legal-required lead-in above the field (per the FigJam legal note). Final wording pends legal
    // sign-off; this mirrors their drafted language.
    attestation:
      "By typing my name below, I confirm that I have read and agree to the telehealth informed consent and consent to treat above, and I voluntarily consent to receive care from Carrot.",
    label: "Type your full name to sign",
    helper: "Typing your name here serves as your electronic signature.",
    requiredMessage: "Please type your full name to sign."
  }
}

// Every consent type the flow knows about — the keys of `consents`, so it can't drift from the copy.
// Used as the safe fallback when the server's per-consent status is unavailable: treat all as
// outstanding so the member can still sign. The cast is sound because `consents` is keyed by
// ConsentType (a missing key wouldn't compile).
export const consentTypes = Object.keys(consentDocument.consents) as ConsentType[]
